import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import LayoutSingleView from '../components/layout/LayoutSingleView'
import { MenuItems } from '../enums/MenuItems'
import tenantApi from '../api/tenant/TenantApi'
import { UserDto, UserTenantMembershipDto } from '../api/tenant/models/UserDto'
import Button from '../uiV2/common/form/inputs/Button'
import { Pages } from '../Pages'
import UserRolePill from '../components/UserRolePill'
import auditApi, { AuditLog, AuditLogStatus } from '../api/audit/AuditApi'
import Badge from '../uiV2/common/badge/Badge'
import { BadgeColor } from '../uiV2/common/badge/badgeTypes'

const formatDateTime = (value?: string | Date) => {
    if (!value) return '—'
    const date = typeof value === 'string' ? new Date(value) : value
    if (isNaN(date.getTime())) return '—'
    return date.toLocaleString()
}

const getDisplayName = (user: UserDto) => {
    if (user.name && user.name.trim().length > 0) {
        return user.name
    }
    const firstName = user.person?.firstName ?? ''
    const lastName = user.person?.lastName ?? ''
    const fallback = `${firstName} ${lastName}`.trim()
    return fallback || user.email
}

const statusBadgeColor = (status: AuditLogStatus): BadgeColor =>
    status === AuditLogStatus.SUCCESS ? BadgeColor.Success : BadgeColor.Error
const PERSON_COLD_STORAGE_TAB = 'person-cold-storage'

const buildColdStorageUrl = (params: {
    search: string
    mode: 'full_name' | 'id'
}): string => {
    const searchParams = new URLSearchParams()
    searchParams.set('tab', PERSON_COLD_STORAGE_TAB)
    searchParams.set('personSearch', params.search)
    searchParams.set('personSearchMode', params.mode)
    return `${Pages.DataManagement}?${searchParams.toString()}`
}

function MembershipList({
    memberships,
}: {
    memberships: UserTenantMembershipDto[]
}) {
    const filtered = (memberships ?? []).filter(
        (membership) => !!membership.tenantId,
    )

    if (!filtered.length) {
        return (
            <div className="text-sm text-gray-500">No tenant memberships.</div>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            {filtered.map((membership) => (
                <div
                    key={membership.tenantId}
                    className="rounded border border-slate-200 p-3 shadow-sm"
                >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-col">
                            <Link
                                to={`${Pages.Tenant}?id=${membership.tenantId}`}
                                className="link-text font-semibold"
                            >
                                {membership.tenant?.name ??
                                    membership.tenantId ??
                                    'Unknown tenant'}
                            </Link>
                            <span className="text-xs text-gray-500">
                                Tenant ID: {membership.tenantId}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {membership.roles?.length
                                ? membership.roles.map((role) => (
                                      <UserRolePill key={role} value={role} />
                                  ))
                                : 'No roles'}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}

type PasswordMessage = {
    type: 'success' | 'error'
    text: string
}

function UserAuditLogSection({
    userId,
}: {
    userId: string
}): React.JSX.Element {
    const [logs, setLogs] = useState<AuditLog[]>([])
    const [page, setPage] = useState(1)
    const [hasNext, setHasNext] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const loadLogs = useCallback(
        async (pageNumber: number, append: boolean) => {
            setIsLoading(true)
            setError(null)
            try {
                const res = await auditApi.getAuditLogs({
                    pageNumber,
                    perPage: 25,
                    sortDirection: 'DESC',
                    filters: { userId },
                })
                setHasNext(res.hasNextPage)
                setLogs((prev) => (append ? [...prev, ...res.logs] : res.logs))
            } catch (err) {
                console.error('Failed to load user audit logs', err)
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to load audit history'
                setError(message)
            } finally {
                setIsLoading(false)
            }
        },
        [userId],
    )

    useEffect(() => {
        void loadLogs(1, false)
        setPage(1)
    }, [loadLogs, userId])

    const handleLoadMore = () => {
        const nextPage = page + 1
        setPage(nextPage)
        void loadLogs(nextPage, true)
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Audit Log</h3>
                <Button
                    type="button"
                    style="secondary"
                    onClick={() => {
                        setPage(1)
                        void loadLogs(1, false)
                    }}
                    enabled={!isLoading}
                >
                    Refresh
                </Button>
            </div>
            {error && (
                <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="main-table w-full min-w-[800px] text-sm">
                    <thead>
                        <tr>
                            <th>Timestamp</th>
                            <th>Event</th>
                            <th>Status</th>
                            <th>Source</th>
                            <th>Auth Strategy</th>
                            <th>Request ID</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && logs.length === 0 && (
                            <tr>
                                <td colSpan={7} className="py-6 text-center">
                                    Loading audit history…
                                </td>
                            </tr>
                        )}
                        {!isLoading && logs.length === 0 && (
                            <tr>
                                <td colSpan={7} className="py-6 text-center">
                                    No audit events recorded for this user yet.
                                </td>
                            </tr>
                        )}
                        {logs.map((log) => (
                            <tr key={log.id}>
                                <td>{formatDateTime(log.timestamp)}</td>
                                <td>{log.eventType}</td>
                                <td>
                                    <Badge color={statusBadgeColor(log.status)}>
                                        {log.status}
                                    </Badge>
                                </td>
                                <td>{log.source ?? '—'}</td>
                                <td>{log.authStrategy ?? '—'}</td>
                                <td className="break-all text-xs">
                                    {log.requestId ?? '—'}
                                </td>
                                <td className="text-xs text-gray-600">
                                    {log.details ?? '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {hasNext && (
                <Button
                    type="button"
                    style="secondary"
                    onClick={handleLoadMore}
                    enabled={!isLoading}
                >
                    Load More
                </Button>
            )}
        </div>
    )
}

function ViewUser(): React.JSX.Element {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const userId = searchParams.get('userId')
    const [user, setUser] = useState<UserDto | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [passwordMessage, setPasswordMessage] =
        useState<PasswordMessage | null>(null)
    const [isSavingPassword, setIsSavingPassword] = useState(false)

    const refreshUser = useCallback(async () => {
        if (!userId) {
            setError('Missing userId in URL')
            setIsLoading(false)
            return
        }
        setIsLoading(true)
        setError(null)
        try {
            const res = await tenantApi.getUser(userId)
            setUser(res.user)
        } catch (err) {
            console.error('Failed to load user', err)
            const message =
                err instanceof Error ? err.message : 'Unable to load user'
            setError(message)
        } finally {
            setIsLoading(false)
        }
    }, [userId])

    useEffect(() => {
        void refreshUser()
    }, [refreshUser])

    const handlePasswordSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ) => {
        event.preventDefault()
        if (!userId) return
        if (password.length < 8) {
            setPasswordMessage({
                type: 'error',
                text: 'Password must be at least 8 characters long.',
            })
            return
        }
        if (password !== confirmPassword) {
            setPasswordMessage({
                type: 'error',
                text: 'Passwords do not match.',
            })
            return
        }
        setIsSavingPassword(true)
        setPasswordMessage(null)
        try {
            await tenantApi.setUserPassword({ userId, password })
            setPassword('')
            setConfirmPassword('')
            setPasswordMessage({
                type: 'success',
                text: 'Password updated successfully.',
            })
        } catch (err) {
            console.error('Failed to set password', err)
            const message =
                err instanceof Error ? err.message : 'Unable to update password'
            setPasswordMessage({ type: 'error', text: message })
        } finally {
            setIsSavingPassword(false)
        }
    }

    const displayName = user ? getDisplayName(user) : ''

    let content: React.ReactNode = null

    if (isLoading) {
        content = <div>Loading user...</div>
    } else if (error) {
        content = (
            <div className="rounded border border-red-200 bg-red-50 p-4 text-red-700">
                {error}
            </div>
        )
    } else if (user) {
        const linkedPersonName =
            user.person?.firstName || user.person?.lastName
                ? `${user.person?.firstName ?? ''} ${
                      user.person?.lastName ?? ''
                  }`.trim()
                : ''
        const linkedPersonLink =
            user.personId && user.personId.length
                ? buildColdStorageUrl({
                      search: user.personId,
                      mode: 'id',
                  })
                : user.person && linkedPersonName
                  ? buildColdStorageUrl({
                        search: linkedPersonName,
                        mode: 'full_name',
                    })
                  : null

        content = (
            <div className="flex flex-col gap-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                        {user.person?.linkedInProfilePicUrl && (
                            <img
                                src={user.person.linkedInProfilePicUrl}
                                alt={displayName}
                                className="h-16 w-16 rounded-full border border-slate-200 object-cover"
                            />
                        )}
                        <div className="flex flex-col gap-1">
                            <button
                                type="button"
                                className="flex items-center gap-1 text-sm text-[#153bab] hover:underline"
                                onClick={() =>
                                    navigate({
                                        pathname: Pages.Tenants,
                                        search: (() => {
                                            const params = new URLSearchParams(
                                                searchParams,
                                            )
                                            params.set('tab', 'users')
                                            return params.toString()
                                        })(),
                                    })
                                }
                            >
                                <span aria-hidden="true">←</span>
                                Back to Users
                            </button>
                            <h1 className="text-2xl font-semibold">
                                {displayName}
                            </h1>
                            <div className="text-sm text-gray-500">
                                {user.email}
                            </div>
                            {user.isProductAdmin && (
                                <UserRolePill isProductAdmin />
                            )}
                        </div>
                    </div>
                    <Button
                        type="button"
                        style="secondary"
                        onClick={() =>
                            navigate(`${Pages.UpdateUser}?userId=${user.id}`)
                        }
                    >
                        Edit User
                    </Button>
                </div>

                <section className="rounded border border-slate-200 p-4 shadow-sm">
                    <h2 className="mb-3 text-lg font-semibold">Account Info</h2>
                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <div className="text-xs font-semibold uppercase text-slate-500">
                                User ID
                            </div>
                            <div className="text-sm break-all">{user.id}</div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase text-slate-500">
                                Created
                            </div>
                            <div className="text-sm">
                                {formatDateTime(user.createdAt)}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase text-slate-500">
                                Active
                            </div>
                            <div className="text-sm">
                                {user.isActive ? 'Yes' : 'No'}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase text-slate-500">
                                Linked Person
                            </div>
                            {user.person ? (
                                linkedPersonLink ? (
                                    <Link
                                        to={linkedPersonLink}
                                        className="link-text text-sm"
                                    >
                                        {user.person.firstName}{' '}
                                        {user.person.lastName}
                                    </Link>
                                ) : (
                                    <div className="text-sm">
                                        {user.person.firstName}{' '}
                                        {user.person.lastName}
                                    </div>
                                )
                            ) : (
                                <div className="text-sm text-gray-500">
                                    Not linked
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <section className="rounded border border-slate-200 p-4 shadow-sm">
                    <h2 className="mb-3 text-lg font-semibold">
                        Tenant Memberships
                    </h2>
                    <MembershipList memberships={user.memberships ?? []} />
                </section>

                <section className="rounded border border-slate-200 p-4 shadow-sm">
                    <h2 className="mb-3 text-lg font-semibold">Set Password</h2>
                    <form
                        className="flex flex-col gap-3 max-w-md"
                        onSubmit={handlePasswordSubmit}
                    >
                        <input
                            type="password"
                            className="form-input"
                            placeholder="New password"
                            value={password}
                            onChange={(event) =>
                                setPassword(event.target.value)
                            }
                        />
                        <input
                            type="password"
                            className="form-input"
                            placeholder="Confirm password"
                            value={confirmPassword}
                            onChange={(event) =>
                                setConfirmPassword(event.target.value)
                            }
                        />
                        <Button
                            type="submit"
                            style="primary"
                            enabled={!isSavingPassword}
                        >
                            {isSavingPassword ? 'Saving…' : 'Update Password'}
                        </Button>
                        {passwordMessage && (
                            <div
                                className={`text-sm ${
                                    passwordMessage.type === 'success'
                                        ? 'text-green-700'
                                        : 'text-red-700'
                                }`}
                            >
                                {passwordMessage.text}
                            </div>
                        )}
                    </form>
                </section>

                <section className="rounded border border-slate-200 p-4 shadow-sm">
                    <UserAuditLogSection userId={user.id} />
                </section>
            </div>
        )
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Users}>
            {content}
        </LayoutSingleView>
    )
}

export default ViewUser
